import { useEffect, useState } from 'react';
import { api, ApiError } from '../../api/client.js';
import { useToast } from '../../context/ToastContext.jsx';
import LoadingState from '../LoadingState.jsx';
import EmptyState from '../EmptyState.jsx';
import StatusBadge from '../StatusBadge.jsx';
import Modal from '../Modal.jsx';
import MaskedParamsEditor from '../MaskedParamsEditor.jsx';

const SEVERITIES = ['low', 'medium', 'high'];
const CLASSIFICATIONS = ['UNKNOWN', 'LOW_RISK', 'SUSPICIOUS', 'HIGH_RISK', 'IVT_CONFIRMED'];
const ACTIONS = ['ALLOW', 'ALLOW_WITH_SIGNAL', 'LOWER_PRIORITY', 'BLOCK'];

export default function IvtRulesPoliciesTab() {
  return (
    <div className="stack">
      <RulesSection />
      <PoliciesSection />
      <ProvidersSection />
    </div>
  );
}

// -- Rules ----------------------------------------------------------------------------------

function RulesSection() {
  const toast = useToast();
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingConfig, setEditingConfig] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setRules(await api.listIvtRules());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load IVT rules.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function toggleEnabled(rule) {
    try {
      await api.patchIvtRule(rule.code, { enabled: !rule.enabled });
      toast.success(`${rule.name} ${!rule.enabled ? 'enabled' : 'disabled'}.`);
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not update rule.');
    }
  }

  async function changeSeverity(rule, severity) {
    try {
      await api.patchIvtRule(rule.code, { severity });
      toast.success(`${rule.name} severity set to ${severity}.`);
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not update severity.');
    }
  }

  return (
    <section className="card">
      <div className="card__header">
        <h2 className="card__title">IVT rules (phase-1 native)</h2>
      </div>
      <p className="card__hint">
        Stateless rules that run inline in auction-service at zero added latency. Thresholds/config
        are operator-configurable; the rule set itself is fixed engineering, not addable from here.
      </p>
      {loading && <LoadingState label="Loading rules" />}
      {!loading && error && <EmptyState title="Couldn't load rules" message={error} />}
      {!loading && !error && rules && (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Rule</th>
                <th>Enabled</th>
                <th>Severity</th>
                <th>Config</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.code}>
                  <td className="cell-title">{r.name}<div className="cell-subtitle">{r.code}</div></td>
                  <td>
                    <label className="switch">
                      <input type="checkbox" checked={r.enabled} onChange={() => toggleEnabled(r)} />
                      <span className="switch__track"><span className="switch__thumb" /></span>
                    </label>
                  </td>
                  <td>
                    <select value={r.severity} onChange={(e) => changeSeverity(r, e.target.value)}>
                      {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="cell-muted">{Object.keys(r.config || {}).length} key(s)</td>
                  <td>
                    <button className="btn btn--ghost btn--sm" onClick={() => setEditingConfig(r)}>Edit config</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingConfig && (
        <JsonConfigModal
          title={`${editingConfig.name} — config`}
          value={editingConfig.config}
          onClose={() => setEditingConfig(null)}
          onSave={async (config) => {
            await api.patchIvtRule(editingConfig.code, { config });
            toast.success('Config updated.');
            setEditingConfig(null);
            load();
          }}
        />
      )}
    </section>
  );
}

// Plain JSON textarea with validation -- ivt_rules.config holds thresholds/tuning, not secrets,
// so (unlike traffic_quality_providers.default_params below) there's no reason to mask it.
function JsonConfigModal({ title, value, onClose, onSave }) {
  const [text, setText] = useState(() => JSON.stringify(value || {}, null, 2));
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  async function handleSave() {
    setFormError(null);
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      setFormError('Not valid JSON.');
      return;
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      setFormError('Config must be a JSON object.');
      return;
    }
    setSaving(true);
    try {
      await onSave(parsed);
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={title} onClose={onClose}>
      <div className="form">
        <textarea rows={10} value={text} onChange={(e) => setText(e.target.value)} style={{ fontFamily: 'var(--font-mono)', fontSize: '12.5px' }} />
        {formError && <div className="form-error">{formError}</div>}
        <div className="form__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn--primary" disabled={saving} onClick={handleSave}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </Modal>
  );
}

// -- Policies (classification -> action decision model) -------------------------------------

function PoliciesSection() {
  const toast = useToast();
  const [publishers, setPublishers] = useState([]);
  const [scope, setScope] = useState('network'); // 'network' | publisher id (string)
  const [defaults, setDefaults] = useState(null);
  const [overrides, setOverrides] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(null); // classification currently saving

  async function loadDefaults() {
    try {
      setDefaults(await api.listIvtPolicies());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load network default policy.');
    }
  }

  async function loadAll() {
    setLoading(true);
    setError(null);
    try {
      const [pubs] = await Promise.all([api.listPublishers().catch(() => [])]);
      setPublishers(pubs);
      await loadDefaults();
      if (scope !== 'network') setOverrides(await api.listIvtPolicies(Number(scope)));
      else setOverrides(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load IVT policies.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (scope === 'network') { setOverrides(null); return; }
    (async () => {
      try {
        setOverrides(await api.listIvtPolicies(Number(scope)));
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : 'Could not load overrides for this publisher.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  const defaultByClassification = Object.fromEntries((defaults || []).map((r) => [r.classification, r.action]));
  const overrideByClassification = Object.fromEntries((overrides || []).map((r) => [r.classification, r.action]));

  async function setAction(classification, action) {
    setSaving(classification);
    try {
      await api.putIvtPolicy({
        publisher_id: scope === 'network' ? null : Number(scope),
        classification,
        action,
      });
      toast.success(`${classification} → ${action}${scope === 'network' ? ' (network default)' : ' (publisher override)'}.`);
      await loadDefaults();
      if (scope !== 'network') setOverrides(await api.listIvtPolicies(Number(scope)));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not update policy.');
    } finally {
      setSaving(null);
    }
  }

  return (
    <section className="card">
      <div className="card__header">
        <h2 className="card__title">IVT policy (classification → action)</h2>
      </div>
      <p className="card__hint">
        Decides what happens to an inbound auction once it's classified. No BLOCK action is seeded
        by default anywhere -- it's real and available, but an operator opts into it deliberately
        once real data justifies it.
      </p>

      <label className="field" style={{ maxWidth: 360 }}>
        <span className="field__label">Scope</span>
        <select value={scope} onChange={(e) => setScope(e.target.value)}>
          <option value="network">Network default</option>
          {publishers.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.domain})</option>)}
        </select>
      </label>

      {loading && <LoadingState label="Loading policy" />}
      {!loading && error && <EmptyState title="Couldn't load policy" message={error} />}
      {!loading && !error && (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Classification</th>
                <th>Network default</th>
                {scope !== 'network' && <th>Override for this publisher</th>}
              </tr>
            </thead>
            <tbody>
              {CLASSIFICATIONS.map((c) => {
                const hasOverride = scope !== 'network' && overrideByClassification[c] !== undefined;
                return (
                  <tr key={c}>
                    <td className="cell-title">{c}</td>
                    <td>
                      {scope === 'network' ? (
                        <select
                          value={defaultByClassification[c] || ''}
                          disabled={saving === c}
                          onChange={(e) => setAction(c, e.target.value)}
                        >
                          {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                        </select>
                      ) : (
                        <StatusBadge status={defaultByClassification[c] || '—'} />
                      )}
                    </td>
                    {scope !== 'network' && (
                      <td>
                        <select
                          value={hasOverride ? overrideByClassification[c] : ''}
                          disabled={saving === c}
                          onChange={(e) => setAction(c, e.target.value)}
                        >
                          <option value="" disabled>{hasOverride ? '' : `Inherit default (${defaultByClassification[c] || '—'})`}</option>
                          {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                        </select>
                        {!hasOverride && <div className="cell-subtitle">No override set for this publisher yet -- selecting an action creates one (there's no "remove override" once set).</div>}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// -- Providers (vendor-neutral registry) -----------------------------------------------------

function ProvidersSection() {
  const toast = useToast();
  const [providers, setProviders] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editingParams, setEditingParams] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setProviders(await api.listTrafficQualityProviders());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load traffic quality providers.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function toggleEnabled(p) {
    try {
      await api.patchTrafficQualityProvider(p.code, { enabled: !p.enabled });
      toast.success(`${p.name} ${!p.enabled ? 'enabled' : 'disabled'}.`);
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not update provider.');
    }
  }

  async function changeMode(p, mode) {
    if (mode === 'active') {
      const confirmed = window.confirm(
        `Setting "${p.name}" to ACTIVE means its result will actually affect real IVT policy decisions on live traffic (not just recorded for comparison). Continue?`
      );
      if (!confirmed) return;
    }
    try {
      await api.patchTrafficQualityProvider(p.code, { mode });
      toast.success(`${p.name} set to ${mode}.`);
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not change mode -- configure default_params first if going active.');
    }
  }

  async function changeTimeout(p, timeoutMs) {
    try {
      await api.patchTrafficQualityProvider(p.code, { timeout_ms: timeoutMs });
      toast.success(`${p.name} timeout set to ${timeoutMs}ms.`);
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not update timeout.');
    }
  }

  return (
    <section className="card">
      <div className="card__header">
        <h2 className="card__title">Traffic quality providers</h2>
        <button className="btn btn--primary btn--sm" onClick={() => setCreating(true)}>+ Add provider</button>
      </div>
      <p className="card__hint">
        Vendor-neutral registry. <code>mode=test</code> means results are recorded for comparison
        only and never affect the real policy decision -- how a not-yet-contracted vendor gets
        wired up without pretending it's production-active.
      </p>

      {loading && <LoadingState label="Loading providers" />}
      {!loading && error && <EmptyState title="Couldn't load providers" message={error} />}
      {!loading && !error && providers && (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Provider</th>
                <th>Enabled</th>
                <th>Mode</th>
                <th className="num">Timeout</th>
                <th>Params</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {providers.map((p) => (
                <tr key={p.code}>
                  <td className="cell-title">{p.name}<div className="cell-subtitle">{p.code}</div></td>
                  <td>
                    <label className="switch">
                      <input type="checkbox" checked={p.enabled} onChange={() => toggleEnabled(p)} />
                      <span className="switch__track"><span className="switch__thumb" /></span>
                    </label>
                  </td>
                  <td>
                    <select value={p.mode} onChange={(e) => changeMode(p, e.target.value)}>
                      <option value="test">test</option>
                      <option value="active">active</option>
                    </select>
                    {p.mode === 'active' && <div className="card__hint card__hint--warning">Live -- affects real policy decisions</div>}
                  </td>
                  <td className="num">
                    <TimeoutInput value={p.timeout_ms} onSave={(v) => changeTimeout(p, v)} />
                  </td>
                  <td className="cell-muted">{Object.keys(p.default_params || {}).length} param(s)</td>
                  <td>
                    <button className="btn btn--ghost btn--sm" onClick={() => setEditingParams(p)}>Params</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingParams && (
        <Modal title={`${editingParams.name} — default_params`} onClose={() => setEditingParams(null)}>
          <p className="card__hint">
            Treated with the same credential hygiene as demand-partner params -- values are never
            re-displayed once saved.
          </p>
          <MaskedParamsEditor
            existingKeys={Object.keys(editingParams.default_params || {})}
            emptyHint="No params configured yet. mode cannot be set to active with empty default_params."
            onSave={async (params) => {
              await api.patchTrafficQualityProvider(editingParams.code, { default_params: params });
              toast.success('Params updated.');
              setEditingParams(null);
              load();
            }}
          />
          <div className="form__actions">
            <button className="btn btn--ghost" onClick={() => setEditingParams(null)}>Close</button>
          </div>
        </Modal>
      )}

      {creating && (
        <CreateProviderModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />
      )}
    </section>
  );
}

function TimeoutInput({ value, onSave }) {
  const [draft, setDraft] = useState(String(value));
  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
      <input
        type="number"
        min="1"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        style={{ width: '70px', padding: '6px 8px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)' }}
      />
      {Number(draft) !== value && Number.isFinite(Number(draft)) && Number(draft) > 0 && (
        <button className="btn btn--ghost btn--sm" onClick={() => onSave(Number(draft))}>Save</button>
      )}
    </div>
  );
}

function CreateProviderModal({ onClose, onCreated }) {
  const toast = useToast();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [timeoutMs, setTimeoutMs] = useState('150');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError(null);
    const trimmedCode = code.trim();
    if (!trimmedCode || !/^[a-z][a-z0-9_]*$/.test(trimmedCode)) {
      setFormError('Code is required and must be lowercase snake_case (e.g. "human_security").');
      return;
    }
    if (!name.trim()) {
      setFormError('Name is required.');
      return;
    }
    const timeout = Number(timeoutMs);
    if (Number.isNaN(timeout) || timeout <= 0) {
      setFormError('Timeout must be a positive number of milliseconds.');
      return;
    }
    setSaving(true);
    try {
      await api.createTrafficQualityProvider({ code: trimmedCode, name: name.trim(), timeout_ms: timeout });
      toast.success(`${name.trim()} created -- disabled, mode=test by default. Configure params, then enable.`);
      onCreated();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Could not create provider.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="Add traffic quality provider" onClose={onClose}>
      <form onSubmit={handleSubmit} className="form">
        <label className="field">
          <span className="field__label">Code</span>
          <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. human_security" autoFocus />
        </label>
        <label className="field">
          <span className="field__label">Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. HUMAN Security" />
        </label>
        <label className="field">
          <span className="field__label">Timeout (ms)</span>
          <input type="number" min="1" value={timeoutMs} onChange={(e) => setTimeoutMs(e.target.value)} />
        </label>
        {formError && <div className="form-error">{formError}</div>}
        <div className="form__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn--primary" disabled={saving}>{saving ? 'Creating…' : 'Create (disabled, test mode)'}</button>
        </div>
      </form>
    </Modal>
  );
}
